import { Box, CircularProgress, Collapse, InputAdornment, Typography, useTheme } from "@mui/material";
import dayjs from "dayjs";
import {
  Archive,
  MessageSquare,
  Plus,
  Search,
  Settings,
  Trash2
} from "lucide-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import oliviaIcon from "../../../../assets/oliviaIcon.png";
import { ButtonDigited } from "../../../../components/Button/Button.variants";
import {
  ConversationDate,
  ConversationItem,
  ConversationItemContent,
  ConversationList,
  ConversationPreview,
  ConversationTitle,
  DeleteIcon,
  EmptyState,
  HistoryDisabledContainer,
  HistoryDisabledIcon,
  HistoryDisabledSubtitle,
  HistoryDisabledTitle,
  LoadingRow,
  NewChatSection,
  SearchInput,
  SearchSection,
  SettingsButton,
  SidebarFooter,
  SidebarHeader,
  SidebarRoot,
  SidebarTitleRow
  
} from "../../AISupportChat.styles";
import { ConversationSummary } from "../../AISupportChat.types";

interface ChatAgentSidebarProps {
  isPrelogin: boolean;
  conversationsList: ConversationSummary[];
  isLoading: boolean;
  activeSessionId: string;
  onSelect: (sessionId: string) => void;
  onNewChat?: () => void;
  onDelete?: (sessionId: string) => void;
  onSettings?: () => void;
  disabled?: boolean;
  historyEnabled?: boolean;
}

// Formato data per le card di conversazione, allineato al mockup:
//   < 1 minuto fa  → "Adesso"
//   stesso giorno  → "Oggi, HH:mm"
//   ieri           → "Ieri, HH:mm"
//   stesso anno    → "DD MMM, HH:mm" (es. "25 Apr, 10:22")
//   altrimenti     → "DD/MM/YY, HH:mm"
const formatRelativeDate = (iso?: string) => {
  if (!iso) return "";
  const d = dayjs(iso);
  if (!d.isValid()) return "";
  const now = dayjs();
  if (now.diff(d, "minute") < 1) return "Adesso";
  if (d.isSame(now, "day")) return `Oggi, ${d.format("HH:mm")}`;
  if (d.isSame(now.subtract(1, "day"), "day"))
    return `Ieri, ${d.format("HH:mm")}`;
  if (d.isSame(now, "year")) return d.format("DD MMM, HH:mm");
  return d.format("DD/MM/YY, HH:mm");
};

const ITEM_ANIMATION_MS = 250;
const ITEM_STAGGER_MS = 40;
const MAX_STAGGERED_ITEMS = 8;

const ChatAgentSidebar = ({
  isPrelogin,
  conversationsList,
  isLoading,
  activeSessionId,
  onSelect,
  onNewChat,
  onDelete,
  onSettings,
  disabled,
  historyEnabled = true
}: ChatAgentSidebarProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return conversationsList;
    return conversationsList.filter((c) => {
      const haystack = `${c.title ?? ""} ${c.preview ?? ""}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [conversationsList, search]);

  const [removingIds, setRemovingIds] = useState<string[]>([]);
  const pendingDeletions = useRef<Map<string, ReturnType<typeof setTimeout>>>(
    new Map()
  );
  const onDeleteRef = useRef(onDelete);
  onDeleteRef.current = onDelete;

  useEffect(() => {
    const timers = pendingDeletions.current;
    return () => {
      timers.forEach((timer, sid) => {
        clearTimeout(timer);
        onDeleteRef.current && onDeleteRef.current(sid);
      });
      timers.clear();
    };
  }, []);

  const handleDelete = (
    e: React.MouseEvent<HTMLButtonElement>,
    sid: string
  ) => {
    e.stopPropagation();
    if (disabled || pendingDeletions.current.has(sid)) return;

    setRemovingIds((prev) => [...prev, sid]);

    const timer = setTimeout(() => {
      pendingDeletions.current.delete(sid);
      setRemovingIds((prev) => prev.filter((id) => id !== sid));
      onDeleteRef.current && onDeleteRef.current(sid);
    }, ITEM_ANIMATION_MS);

    pendingDeletions.current.set(sid, timer);
  };
  if(isPrelogin) return <></>;
  return (
    <SidebarRoot theme={ theme }>
      <SidebarHeader>
        <SidebarTitleRow>
          <Box
            component="img"
            src={ oliviaIcon }
            alt=""
            aria-hidden="true"
            sx={ {
              flexShrink: 0,
              height: "40px",
              width: "40px"
            } }
          />
          <Typography
            variant="h4"
            component={ "h2" }
            fontWeight={ 700 }
            fontSize={ "1.125rem" }
          >
            { t("ai_support_chat_title") }
          </Typography>
        </SidebarTitleRow>
        <NewChatSection>
          <ButtonDigited 
            typology="primary"
            disabled={ disabled }
            endIcon={ <Plus size={ "1.125rem" } /> }
            onClick={ onNewChat }
            text={ t("new_chat") }
            sx={ {
              width: "100% !important"
            } }
          />
        </NewChatSection>
        { historyEnabled && (
          <SearchSection>
            <SearchInput
              fullWidth
              size="small"
              placeholder={ t("search_agent_history_placeholder") || "" }
              value={ search }
              onChange={ (e) => setSearch(e.target.value) }
              InputProps={ {
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={ 16 } color={ theme.customColors.border }/>
                  </InputAdornment>
                )
              } }
            />
          </SearchSection>
        ) }
      </SidebarHeader>

      <ConversationList withTopBorder={ historyEnabled }>
        { !historyEnabled ? (
          <HistoryDisabledContainer>
            <HistoryDisabledIcon>
              <Archive size={ 20 } />
            </HistoryDisabledIcon>
            <HistoryDisabledTitle>
              { t("historyDisabledTitle") }
            </HistoryDisabledTitle>
            <HistoryDisabledSubtitle>
              { t("historyDisabledSubtitle") }
            </HistoryDisabledSubtitle>
          </HistoryDisabledContainer>
        ) : isLoading && conversationsList.length === 0 ? (
          <LoadingRow>
            <CircularProgress size={ 18 } />
          </LoadingRow>
        ) : filtered.length === 0 ? (
          <EmptyState>{ t("ai_no_conversations") }</EmptyState>
        ) : (
          <>
            { filtered.map((c, index) => {
              const isActive = c.session_id === activeSessionId;
              const title =
                    c.title?.trim() ||
                    (c.message_count && c.message_count > 0
                      ? t("conversation")
                      : t("newConversation"));
              const preview = c.preview?.trim() || "";
              const date = formatRelativeDate(c.updated_at ?? c.created_at);
              const isRemoving = removingIds.includes(c.session_id);
              return (
                <Collapse
                  key={ c.session_id }
                  in={ !isRemoving }
                  appear
                  timeout={ ITEM_ANIMATION_MS }
                  style={ {
                    transitionDelay: isRemoving
                      ? "0ms"
                      : `${Math.min(index, MAX_STAGGERED_ITEMS) * ITEM_STAGGER_MS}ms`
                  } }
                >
                  <ConversationItem
                    active={ isActive }
                    onClick={ () => onSelect(c.session_id) }
                    sx={ {
                      opacity: isRemoving ? 0 : 1,
                      transition: `opacity ${ITEM_ANIMATION_MS}ms ease`
                    } }
                  >
                    <MessageSquare
                      size={ "1.125rem" }
                      color={ theme.customColors.accentVariantA }
                    />
                    <ConversationItemContent>
                      <ConversationTitle>{ title }</ConversationTitle>
                      <ConversationPreview>{ preview }</ConversationPreview>
                      { date && <ConversationDate>{ date }</ConversationDate> }
                    </ConversationItemContent>
                    <DeleteIcon
                      size="small"
                      aria-label={ t("deleteConversation") || "Delete" }
                      onClick={ (e) => handleDelete(e, c.session_id) }
                      disabled={ disabled || isRemoving }
                    >
                      <Trash2 
                        size={ "1.125rem" }
                      />
                    </DeleteIcon>
                  </ConversationItem>
                </Collapse>
              );
            }) }
          </>
        ) }
      </ConversationList>

      { onSettings && (
        <SidebarFooter>
          <SettingsButton onClick={ onSettings } disabled={ disabled }>
            <Settings size={ 18 } />
            { t("settings") }
          </SettingsButton>
        </SidebarFooter>
      ) }
    </SidebarRoot>
  );
};

export default ChatAgentSidebar;
